import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Crown, UserCircle2, Bookmark, Settings as SettingsIcon, LogOut, ChevronRight,
  Activity, Sparkles, Mail, Calendar, ShieldCheck, Bell,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function Row({ icon: Icon, label, sub, onClick, testId, danger, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="tile"
      data-testid={testId}
      style={{ flexDirection: "row", alignItems: "center", textAlign: "left", width: "100%" }}
    >
      <div className="tile-icon">
        <Icon size={18} strokeWidth={1.7} color={danger ? "var(--danger)" : undefined} />
      </div>
      <div style={{ flex: 1 }}>
        <div className="h3" style={{ color: danger ? "var(--danger)" : "var(--text)" }}>
          {label}{badge && <span className="premium-pill" style={{ marginLeft: 8 }}>{badge}</span>}
        </div>
        {sub && <div className="tiny" style={{ marginTop: 4, color: "var(--text-faint)" }}>{sub}</div>}
      </div>
      <ChevronRight size={18} strokeWidth={1.5} color="var(--text-faint)" />
    </button>
  );
}

export default function AccountScreen() {
  const navigate = useNavigate();
  const { user, logout, refresh } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profileComplete, setProfileComplete] = useState(false);
  const [premium, setPremium] = useState(false);

  // ---- Stripe checkout return polling --------------------------------
  // After a successful Stripe Checkout the browser is redirected to
  // /profile?session_id={CHECKOUT_SESSION_ID}. We poll the backend until
  // the payment status flips to "paid" (or times out / fails), then flip
  // the user's premium flag locally. Per Emergent playbook (no webhook
  // dependency for the happy path — webhook is the failsafe).
  const [paymentBanner, setPaymentBanner] = useState(null); // { state, message }
  const pollAttemptsRef = useRef(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const MAX_ATTEMPTS = 12;
    const INTERVAL_MS = 2000;
    setPaymentBanner({ state: "pending", message: "Confirming your payment…" });

    const poll = async () => {
      if (cancelled) return;
      pollAttemptsRef.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data?.status === "paid" || data?.payment_status === "paid") {
          if (cancelled) return;
          setPaymentBanner({ state: "success", message: "Welcome to Nova Premium." });
          await refresh();
          // strip ?session_id= so refresh doesn't re-poll
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        if (data?.status === "expired" || data?.status === "failed") {
          if (cancelled) return;
          setPaymentBanner({ state: "error", message: "Payment did not go through. You're still on the free plan." });
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        if (pollAttemptsRef.current >= MAX_ATTEMPTS) {
          if (cancelled) return;
          setPaymentBanner({ state: "warn", message: "Still confirming with Stripe. Refresh in a moment to see your Premium status." });
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        if (pollAttemptsRef.current < MAX_ATTEMPTS) {
          setTimeout(poll, INTERVAL_MS);
        } else {
          setPaymentBanner({ state: "error", message: "Could not verify the payment. Please contact support if you were charged." });
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh, searchParams, setSearchParams]);

  useEffect(() => {
    api.get("/profile").then((r) => setProfileComplete(!!r.data?.complete)).catch(() => {});
    setPremium(!!user?.premium);
  }, [user]);

  // Initials for the fallback avatar. NEVER a random person's face.
  //   "Chris"          → "C"
  //   "John Smith"     → "JS"
  //   "mary jane cole" → "MJ"  (first 2 word initials)
  //   ""               → "G"   (Guest)
  const initials = (() => {
    const raw = (user?.name || "").trim();
    if (raw) {
      const parts = raw.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0][0].toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    const email = (user?.email || "").trim();
    if (email) return email[0].toUpperCase();
    return "G";
  })();

  return (
    <div className="screen with-nav fade-in" data-testid="account-screen">
      <div className="top-bar">
        <div className="logo-mark">PROFILE</div>
        <button className="icon-btn" onClick={() => navigate("/settings")} aria-label="Settings" data-testid="account-settings-icon">
          <SettingsIcon size={17} strokeWidth={1.7} />
        </button>
      </div>

      {paymentBanner && (
        <div
          className="card"
          data-testid={`account-payment-banner-${paymentBanner.state}`}
          style={{
            padding: 14,
            marginBottom: 14,
            border: "1px solid var(--line)",
            background:
              paymentBanner.state === "success" ? "var(--gold-soft)" :
              paymentBanner.state === "error"   ? "rgba(200,74,74,0.10)" :
              "var(--bg-elev-2)",
            borderColor:
              paymentBanner.state === "success" ? "var(--gold-line)" :
              paymentBanner.state === "error"   ? "rgba(200,74,74,0.32)" :
              "var(--line)",
          }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {paymentBanner.state === "pending" && <span className="spinner" />}
            {paymentBanner.state === "success" && <Crown size={16} color="var(--gold)" />}
            {paymentBanner.state === "error"   && <Sparkles size={14} color="var(--danger, #c84a4a)" />}
            <div className="body-sm" style={{ color: "var(--text)" }}>
              {paymentBanner.message}
            </div>
          </div>
        </div>
      )}

      {/* Hero — user identity (tappable → Edit Account) */}
      <button
        type="button"
        onClick={() => navigate("/account/edit")}
        className="glow-empty-hero"
        data-testid="account-hero"
        aria-label="Edit account information"
        style={{
          display: "flex", alignItems: "center", gap: 16, padding: "22px 22px",
          width: "100%", textAlign: "left", cursor: "pointer",
          background: "var(--bg-elev-1, #141414)",
          border: "1px solid var(--line, #1f1f1f)",
          borderRadius: 14,
        }}
      >
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          background: user?.avatar ? "#000" : "var(--gold-soft)",
          border: "1px solid var(--gold-line)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--gold)", fontFamily: "'Fraunces', serif", fontSize: 28,
          overflow: "hidden", flexShrink: 0,
        }} data-testid="account-avatar">
          {user?.avatar ? (
            <img
              src={`data:image/jpeg;base64,${user.avatar}`}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span style={{ fontSize: initials.length > 1 ? 22 : 28, letterSpacing: 0.5 }}>
              {initials}
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="h3" style={{ color: "var(--text)" }} data-testid="account-name">
            {user?.name || user?.email?.split("@")[0] || "Guest"}
          </div>
          <div className="tiny" style={{ color: "var(--text-faint)", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
            <Mail size={11} strokeWidth={1.8} />
            <span data-testid="account-email" style={{ textTransform: "lowercase" }}>{user?.email || "—"}</span>
          </div>
          <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
            <span
              className="premium-pill"
              data-testid={premium ? "account-plan-premium" : "account-plan-free"}
              style={{ textTransform: "none", letterSpacing: "0.02em", fontSize: 11 }}
            >
              Current Plan: {premium ? "Premium" : "Free"}
            </span>
            {profileComplete && (
              <span className="premium-pill" style={{ borderColor: "rgba(184,255,208,0.32)", color: "var(--good)" }} data-testid="account-profile-complete">
                PROFILE ACTIVE
              </span>
            )}
          </div>
        </div>
        <ChevronRight size={18} strokeWidth={1.5} color="var(--text-faint)" />
      </button>

      {/* Account actions */}
      <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Row
          icon={UserCircle2}
          label="Build My Profile"
          sub="Update name, basics, goals, preferences, medical — AI auto-personalises"
          onClick={() => navigate("/profile-setup")}
          testId="account-row-build-profile"
          badge={!profileComplete ? "SET UP" : null}
        />
        <Row
          icon={Activity}
          label="My Glow-Up Progress"
          sub="Score, photos, weekly report"
          onClick={() => navigate("/progress")}
          testId="account-row-progress"
        />
        <Row
          icon={Bookmark}
          label="My Saved Outfits"
          sub="Your wardrobe history"
          onClick={() => navigate("/saved")}
          testId="account-row-saved"
        />
        <Row
          icon={Crown}
          label="Premium"
          sub={premium ? "Manage your subscription" : "Unlock unlimited outfits & advanced tools"}
          onClick={() => navigate("/premium")}
          testId="account-row-premium"
          badge={premium ? "ACTIVE" : null}
        />
        <Row
          icon={Bell}
          label="Notifications"
          sub="Reminders, push notifications, quiet hours"
          onClick={() => navigate("/settings/reminders")}
          testId="account-row-notifications"
        />
        <Row
          icon={SettingsIcon}
          label="Settings"
          sub="Theme, billing, account"
          onClick={() => navigate("/settings")}
          testId="account-row-settings"
        />
        <Row
          icon={ShieldCheck}
          label="Privacy & Data"
          sub="How your photos & data are handled"
          onClick={() => navigate("/settings#privacy")}
          testId="account-row-privacy"
        />
      </div>

      <button
        type="button"
        className="btn btn-ghost"
        style={{ marginTop: 18, width: "100%", justifyContent: "center" }}
        onClick={logout}
        data-testid="account-signout-btn"
      >
        <LogOut size={14} /> Sign out
      </button>

      <div className="tiny" style={{ marginTop: 18, color: "var(--text-faint)", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        <Sparkles size={10} /> Nova — your daily image coach
        <Calendar size={10} style={{ marginLeft: 6 }} /> 2026
      </div>
    </div>
  );
}
